import jsPDF from 'jspdf';
import { BasePDFOptions } from '..';
import { SRBReport } from '@/types/pos/srb-report';

const formatCurrency = (amount?: number | null): string => {
  return new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

const formatDate = (value?: string): string => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const truncateText = (value: unknown, maxLength: number): string => {
  const text = value === null || value === undefined ? '' : String(value);
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

export async function taxReportTemplate(
  options: BasePDFOptions<SRBReport[]>
): Promise<void> {
  const {
    data,
    filename = 'tax-report.pdf',
    title = 'Tax Report',
    summary,
  } = options;

  const pdf = new jsPDF('l', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 8;
  let yPosition = margin;

  pdf.setFillColor(30, 41, 59);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, margin, 10);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  const generatedAt = new Date();
  pdf.text(
    `Generated: ${generatedAt.toLocaleDateString('en-US')} at ${generatedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`,
    margin,
    17
  );
  pdf.text(`Total Records: ${summary?.totalCount || data.length}`, pageWidth - margin, 10, { align: 'right' });
  pdf.text(`Filtered: ${summary?.filteredCount || data.length}`, pageWidth - margin, 17, { align: 'right' });

  yPosition = 34;

  const headers = [
    'TXN #',
    'Tax #',
    'Txn Receipt #',
    'Date',
    'Net',
    'Discount',
    'Subtotal',
    'Tax Rate',
    'Tax',
    'Total',
    'Submission',
    'Tax Type',
    'Payment',
  ];

  const colWidths = [24, 24, 30, 20, 21, 21, 21, 17, 21, 23, 23, 18, 29];
  const tableWidth = colWidths.reduce((sum, width) => sum + width, 0);
  const tableStartX = (pageWidth - tableWidth) / 2;
  const rowHeight = 7;

  const drawHeader = () => {
    pdf.setFillColor(241, 245, 249);
    pdf.rect(tableStartX, yPosition, tableWidth, 8, 'F');
    pdf.setDrawColor(203, 213, 225);
    pdf.setLineWidth(0.5);
    pdf.line(tableStartX, yPosition + 8, tableStartX + tableWidth, yPosition + 8);

    pdf.setTextColor(51, 65, 85);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    let xPosition = tableStartX;
    headers.forEach((header, index) => {
      const isNumeric = index >= 4 && index <= 9;
      const padding = 1.5;
      if (isNumeric) {
        pdf.text(header, xPosition + colWidths[index] - padding, yPosition + 5, { align: 'right' });
      } else {
        pdf.text(header, xPosition + padding, yPosition + 5);
      }
      xPosition += colWidths[index];
    });
    yPosition += 8;
  };

  drawHeader();

  pdf.setFontSize(6.5);
  pdf.setFont('helvetica', 'normal');

  data.forEach((row, rowIndex) => {
    if (yPosition > pageHeight - 22) {
      pdf.addPage();
      yPosition = margin + 5;
      drawHeader();
      pdf.setFontSize(6.5);
      pdf.setFont('helvetica', 'normal');
    }

    if (rowIndex % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(tableStartX, yPosition, tableWidth, rowHeight, 'F');
    }

    const rowData = [
      truncateText(row.receipt_number, 16),
      truncateText(row.tax_number, 16),
      truncateText(row.transaction_receipt_number, 20),
      formatDate(row.created_at),
      formatCurrency(row.sub_total),
      row.discount > 0 ? formatCurrency(row.discount) : '',
      formatCurrency(Number(row.sub_total) - Number(row.discount)),
      truncateText(row.tax_rate, 8),
      formatCurrency(row.tax_amount),
      formatCurrency(row.total),
      row.tax_submission_type === 'Refund' ? 'Returned' : truncateText(row.tax_submission_type, 16),
      truncateText(row.tax_type, 12),
    ];

    let xPosition = tableStartX;
    rowData.forEach((cellValue, cellIndex) => {
      const isNumeric = cellIndex >= 4 && cellIndex <= 9;
      const padding = 1.5;
      pdf.setTextColor(isNumeric ? 15 : 71, isNumeric ? 23 : 85, isNumeric ? 42 : 105);
      if (isNumeric) {
        pdf.text(String(cellValue), xPosition + colWidths[cellIndex] - padding, yPosition + 5, { align: 'right' });
      } else {
        pdf.text(String(cellValue), xPosition + padding, yPosition + 5);
      }
      xPosition += colWidths[cellIndex];
    });

    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(tableStartX, yPosition + rowHeight, tableStartX + tableWidth, yPosition + rowHeight);
    yPosition += rowHeight;
  });

  const totalPages = pdf.internal.pages.length - 1;
  for (let page = 1; page <= totalPages; page++) {
    pdf.setPage(page);
    pdf.setFillColor(249, 250, 251);
    pdf.rect(0, pageHeight - 14, pageWidth, 14, 'F');
    pdf.setTextColor(100, 116, 139);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text('This is a computer-generated document. No signature is required.', pageWidth / 2, pageHeight - 9, { align: 'center' });
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Page ${page} of ${totalPages}`, pageWidth - margin, pageHeight - 4, { align: 'right' });
  }

  pdf.save(filename);
}
